import { apiFetch } from './client'
import { ApiError } from './errors'
import type { DescriptionBlock, DescriptionStatus, ItemRequirement, VersionSummary } from './jobDescription'

/**
 * Tela 6 (sessao 43, F1) - editor do RASCUNHO da descricao do cargo, parte MANUAL.
 *
 * Contratos (lidos na DEFINICAO, modules/job_architecture/api/description_router.py,
 * application/dtos.py e apps/api/exception_handlers.py):
 *   POST   /api/jobs/{job_id}/descriptions/draft             {source: empty|published} -> versao (201)
 *   GET    /api/job-descriptions/{id}                         -> versao completa
 *   PATCH  /api/job-descriptions/{id}/header                  PATCH por PRESENCA (ausente nao muda; null limpa)
 *   POST   /api/job-descriptions/{id}/items                   {block, text, requirement?, serves_to_item_id?}
 *   PATCH  /api/job-descriptions/{id}/items/{item_id}         parcial por presenca
 *   POST   /api/job-descriptions/{id}/items/{item_id}/move    {new_position} (0-based no bloco)
 *   DELETE /api/job-descriptions/{id}/items/{item_id}         204; ENTREGA com atividades -> 409 + `count`
 *   POST   /api/job-descriptions/{id}/publish                 -> versao publicada (imutavel)
 * Erros com campo proprio: 409 `count` (I3/Q4) e 422 `field` = "area" | "subordinacao" (I7).
 * Fora (F2-b, outra sessao): items/from-draft (itens da trilha de IA).
 *
 * D7 (s38): ids de usuario NAO entram nos tipos. Area e subordinacao vao ao servidor como ID;
 * o NOME gravado e o do lookup no servidor (retrato da versao), nunca o digitado.
 */

export interface DraftItem {
  id: string
  block: DescriptionBlock
  position: number
  text: string
  requirement: ItemRequirement | null
  servesToItemId: string | null
}

export interface DescriptionVersion {
  id: string
  jobId: string
  versionNumber: number
  status: DescriptionStatus
  funcao: string | null
  areaUnitId: string | null
  areaName: string | null
  reportsToJobId: string | null
  reportsToName: string | null
  items: DraftItem[]
}

interface ItemResponse {
  id: string
  block: DescriptionBlock
  position: number
  text: string
  requirement: ItemRequirement | null
  serves_to_item_id: string | null
}

interface VersionResponse {
  id: string
  job_id: string
  version_number: number
  status: DescriptionStatus
  funcao: string | null
  area_unit_id: string | null
  area_unit_name: string | null
  reports_to_job_id: string | null
  reports_to_job_name: string | null
  items: ItemResponse[]
}

export type DraftSource = 'empty' | 'published'

/** PATCH por presenca: so as chaves PRESENTES vao ao servidor; `null` limpa. */
export interface HeaderChanges {
  funcao?: string | null
  areaUnitId?: string | null
  reportsToJobId?: string | null
}

export interface NewItem {
  block: DescriptionBlock
  text: string
  requirement?: ItemRequirement
  servesToItemId?: string | null
}

export interface ItemChanges {
  text?: string
  requirement?: ItemRequirement | null
  servesToItemId?: string | null
}

export type HeaderErrorField = 'area' | 'subordinacao'

/** Blocos que sao paragrafo unico no PES.001 (I1: no maximo 1 item). */
export const SINGLE_ITEM_BLOCKS: readonly DescriptionBlock[] = ['papel', 'experiencia']
/** Blocos cujos itens levam exigencia (necessario | desejavel). */
export const REQUIREMENT_BLOCKS: readonly DescriptionBlock[] = ['formacao', 'conhecimentos', 'treinamentos']
/**
 * Ordem canonica de apresentacao (layout do PES.001.Rev_00). s69 / 2n (Marcelo 28/10/2026 15:28, "deixar formacao acima
 * de hard skills"): o editor passa a seguir a MESMA ordem da ficha e do PDF -- Formacao antes de Hard skills.
 */
export const BLOCK_ORDER: readonly DescriptionBlock[] = [
  'papel',
  'entregas',
  'detalhamento',
  'formacao',
  'conhecimentos',
  'treinamentos',
  'experiencia',
  'soft_skills',
]

function toVersion(r: VersionResponse): DescriptionVersion {
  // Campo a campo, de proposito: nenhum id de usuario (D7) entra no tipo.
  return {
    id: r.id,
    jobId: r.job_id,
    versionNumber: r.version_number,
    status: r.status,
    funcao: r.funcao,
    areaUnitId: r.area_unit_id,
    areaName: r.area_unit_name,
    reportsToJobId: r.reports_to_job_id,
    reportsToName: r.reports_to_job_name,
    items: r.items.map((i) => ({
      id: i.id,
      block: i.block,
      position: i.position,
      text: i.text,
      requirement: i.requirement,
      servesToItemId: i.serves_to_item_id,
    })),
  }
}

const versao = (id: string) => `/api/job-descriptions/${encodeURIComponent(id)}`
const item = (id: string, itemId: string) => `${versao(id)}/items/${encodeURIComponent(itemId)}`

export async function fetchDescription(id: string): Promise<DescriptionVersion> {
  return toVersion(await apiFetch<VersionResponse>(versao(id)))
}

export async function openDraft(jobId: string, source: DraftSource): Promise<DescriptionVersion> {
  return toVersion(
    await apiFetch<VersionResponse>(`/api/jobs/${encodeURIComponent(jobId)}/descriptions/draft`, {
      method: 'POST',
      body: { source },
    }),
  )
}

export async function updateHeader(id: string, changes: HeaderChanges): Promise<DescriptionVersion> {
  const body: Record<string, string | null> = {}
  if ('funcao' in changes) body.funcao = changes.funcao ?? null
  if ('areaUnitId' in changes) body.area_unit_id = changes.areaUnitId ?? null
  if ('reportsToJobId' in changes) body.reports_to_job_id = changes.reportsToJobId ?? null
  return toVersion(await apiFetch<VersionResponse>(`${versao(id)}/header`, { method: 'PATCH', body }))
}

export async function addItem(id: string, novo: NewItem): Promise<DescriptionVersion> {
  const body: Record<string, string | null> = { block: novo.block, text: novo.text }
  if (novo.requirement !== undefined) body.requirement = novo.requirement
  if (novo.servesToItemId !== undefined) body.serves_to_item_id = novo.servesToItemId
  return toVersion(await apiFetch<VersionResponse>(`${versao(id)}/items`, { method: 'POST', body }))
}

/** s59: inclui um item APROVADO/EDITADO da trilha de IA (I6/R13; o servidor usa o texto final da decisao). */
export async function addItemFromDraft(
  id: string,
  ref: { roundId: string; draftItemId: string; requirement?: ItemRequirement; servesToItemId?: string },
): Promise<DescriptionVersion> {
  const body: Record<string, string> = { round_id: ref.roundId, draft_item_id: ref.draftItemId }
  if (ref.requirement !== undefined) body.requirement = ref.requirement
  if (ref.servesToItemId !== undefined) body.serves_to_item_id = ref.servesToItemId
  return toVersion(await apiFetch<VersionResponse>(`${versao(id)}/items/from-draft`, { method: 'POST', body }))
}

export async function editItem(id: string, itemId: string, changes: ItemChanges): Promise<DescriptionVersion> {
  const body: Record<string, string | null> = {}
  if ('text' in changes && changes.text !== undefined) body.text = changes.text
  if ('requirement' in changes) body.requirement = changes.requirement ?? null
  if ('servesToItemId' in changes) body.serves_to_item_id = changes.servesToItemId ?? null
  return toVersion(await apiFetch<VersionResponse>(item(id, itemId), { method: 'PATCH', body }))
}

export async function moveItem(id: string, itemId: string, newPosition: number): Promise<DescriptionVersion> {
  return toVersion(
    await apiFetch<VersionResponse>(`${item(id, itemId)}/move`, { method: 'POST', body: { new_position: newPosition } }),
  )
}

export async function removeItem(id: string, itemId: string): Promise<void> {
  await apiFetch<unknown>(item(id, itemId), { method: 'DELETE' })
}

export async function publishDescription(id: string): Promise<DescriptionVersion> {
  return toVersion(await apiFetch<VersionResponse>(`${versao(id)}/publish`, { method: 'POST' }))
}

// ------------------------------------------------------------------ leitura de erro

function detalhe(e: unknown, status: number): Record<string, unknown> | null {
  if (!(e instanceof ApiError) || e.status !== status) return null
  const d = e.detail
  return typeof d === 'object' && d !== null ? (d as Record<string, unknown>) : null
}

/** 409 do I3: quantas atividades estao ligadas a ENTREGA que se tentou apagar; senao null. */
export function activitiesLinkedCount(e: unknown): number | null {
  const d = detalhe(e, 409)
  return d !== null && typeof d.count === 'number' ? d.count : null
}

/** 422 do I7: qual campo do cabecalho o servidor recusou; senao null. */
export function headerErrorField(e: unknown): HeaderErrorField | null {
  const d = detalhe(e, 422)
  return d !== null && (d.field === 'area' || d.field === 'subordinacao') ? d.field : null
}

// ------------------------------------------------------------------ regras da tela

/** Editar = copia da PUBLICADA vigente quando ela existe (I4); senao, comeca vazio. */
export function draftSource(versions: readonly VersionSummary[]): DraftSource {
  return versions.some((v) => v.status === 'publicada') ? 'published' : 'empty'
}

/** Ligacao atividade -> entrega (B3): so itens do bloco ENTREGAS desta versao, na ordem. */
export function linkableDeliverables(items: readonly DraftItem[]): DraftItem[] {
  return items.filter((i) => i.block === 'entregas').sort((a, z) => a.position - z.position)
}

/**
 * s69 / 2o (Marcelo 28/10/2026 15:28/16:01, "V2 sim ; antes de sexta"): atividades do detalhamento SEM entrega
 * (transversais) -- o editor convida a pedir entregas para elas.
 */
export function transversalActivities(items: readonly DraftItem[]): DraftItem[] {
  return itemsOfBlock(items, 'detalhamento').filter((i) => i.servesToItemId === null)
}

/** I1: papel e experiencia aceitam no maximo 1 item. */
export function canAddItem(block: DescriptionBlock, items: readonly DraftItem[]): boolean {
  if (!SINGLE_ITEM_BLOCKS.includes(block)) return true
  return !items.some((i) => i.block === block)
}

export function itemsOfBlock(items: readonly DraftItem[], block: DescriptionBlock): DraftItem[] {
  return items.filter((i) => i.block === block).sort((a, z) => a.position - z.position)
}

export const descriptionKey = (id: string) => ['job-description', id] as const
